import ReactMarkdown, { type Components, defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { cn } from '../base/utils';

const markdownComponents: Components = {
  h1: ({ children }) => <h1 className="mb-3 mt-6 text-xl font-bold first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="mb-3 mt-6 text-lg font-semibold first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="mb-2 mt-5 text-base font-semibold first:mt-0">{children}</h3>,
  h4: ({ children }) => <h4 className="mb-2 mt-4 text-sm font-semibold first:mt-0">{children}</h4>,
  p: ({ children }) => <p className="my-3 break-words leading-6 first:mt-0 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="my-3 list-disc space-y-1 pl-6">{children}</ul>,
  ol: ({ children }) => <ol className="my-3 list-decimal space-y-1 pl-6">{children}</ol>,
  li: ({ children }) => <li className="break-words leading-6">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="my-4 border-l-4 border-border bg-muted/35 px-4 py-1 text-muted-foreground">
      {children}
    </blockquote>
  ),
  a: ({ children, href }) => (
    <a
      className="break-all font-medium text-primary underline-offset-4 hover:underline"
      href={href}
      target="_blank"
      rel="noreferrer noopener"
    >
      {children}
    </a>
  ),
  pre: ({ children }) => (
    <pre className="my-4 max-w-full overflow-x-auto rounded-lg border border-border/60 bg-muted/45 p-4 text-xs leading-5">
      {children}
    </pre>
  ),
  code: ({ className, children }) => (
    <code
      className={cn(
        className,
        'break-words rounded bg-muted px-1 py-0.5 font-mono text-[0.9em] [pre_&]:break-normal [pre_&]:bg-transparent [pre_&]:p-0',
      )}
    >
      {children}
    </code>
  ),
  table: ({ children }) => (
    <div className="my-4 max-w-full overflow-x-auto rounded-lg border border-border/60">
      <table className="min-w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-muted/55">{children}</thead>,
  tbody: ({ children }) => <tbody className="divide-y divide-border/60">{children}</tbody>,
  tr: ({ children }) => <tr className="divide-x divide-border/60">{children}</tr>,
  th: ({ children }) => <th className="whitespace-nowrap px-3 py-2 text-left font-semibold">{children}</th>,
  td: ({ children }) => <td className="min-w-32 px-3 py-2 align-top">{children}</td>,
  hr: () => <hr className="my-6 border-border" />,
};

export function MarkdownContent({ content, className }: { content: string; className?: string }) {
  const markdown = String(content || '').trim();

  if (!markdown) {
    return <div className={cn('text-sm text-muted-foreground', className)}>—</div>;
  }

  return (
    <div className={cn('min-w-0 max-w-full text-sm text-foreground', className)} data-monkeys-component="markdown-content">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={markdownComponents}
        skipHtml
        urlTransform={defaultUrlTransform}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}
